import { useEffect, useState } from "react";
import { Activity, Clock, MousePointerClick, Flame, UserCheck, UserX, Eye, ShieldCheck, Triangle, Check, Download, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TEMP = {
  chaud: { label: "Chaud", cls: "bg-[#B23A48]/12 text-[#B23A48]" },
  tiede: { label: "Tiède", cls: "bg-[#C89A3E]/18 text-[#8A5A1E]" },
  froid: { label: "Froid", cls: "bg-[#012141]/8 text-[#012141]/70" },
};

const fmtTime = (ms) => {
  const m = Math.floor((ms || 0) / 60000);
  const s = Math.round(((ms || 0) % 60000) / 1000);
  return m > 0 ? `${m} min ${s}s` : `${s}s`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const ScoreBadge = ({ score, temperature }) => {
  const t = TEMP[temperature] || TEMP.froid;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${t.cls}`}>
      <Flame size={12} /> {score} · {t.label}
    </span>
  );
};

export const ActivityPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sendingCard, setSendingCard] = useState(false);

  useEffect(() => {
    api.adminActivity().then(setRows).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openDetail = async (vid) => {
    setOpen(true); setDetail(null);
    try { setDetail(await api.activityDetail(vid)); } catch { /* ignore */ }
  };

  const downloadCard = async (vid, prenom) => {
    try {
      const blob = await api.activityCardPdf(vid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Carte-protection-${prenom || "visiteur"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Carte de protection téléchargée.");
    } catch { toast.error("Carte indisponible pour ce visiteur."); }
  };

  const emailCard = async (vid) => {
    setSendingCard(true);
    try {
      const r = await api.emailCard(vid);
      toast.success(`Carte envoyée à ${r.sent_to}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible.");
    } finally { setSendingCard(false); }
  };

  const tempCounts = {
    chaud: rows.filter((r) => r.temperature === "chaud").length,
    tiede: rows.filter((r) => r.temperature === "tiede").length,
    froid: rows.filter((r) => r.temperature === "froid").length,
  };
  const filtered = filter === "all" ? rows : rows.filter((r) => r.temperature === filter);
  const FILTERS = [["all", "Tous", rows.length], ["chaud", "Chaud", tempCounts.chaud], ["tiede", "Tiède", tempCounts.tiede], ["froid", "Froid", tempCounts.froid]];

  return (
    <div className="mt-8 space-y-4" data-testid="admin-activity-panel">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Activity size={16} style={{ color: "#C89A3E" }} />
        Suivi d'activité des visiteurs — score d'engagement combinant volume, intention d'achat et récence. Votre propre navigation n'est pas comptée.
      </div>

      {!loading && rows.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="activity-filter-bar">
          {FILTERS.map(([id, label, n]) => (
            <button key={id} data-testid={`activity-filter-${id}`} onClick={() => setFilter(id)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${filter === id ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
              {label} <span className={filter === id ? "text-[#F5C64E]" : "text-slate-400"}>{n}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm py-10 text-center">Chargement de l'activité…</p>
      ) : rows.length === 0 ? (
        <p className="text-slate-400 text-sm py-10 text-center">Aucune activité enregistrée pour l'instant.</p>
      ) : (
        <div className="rounded-3xl border border-[#012141]/10 bg-white p-4 sm:p-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-[#012141]/10">
                <th className="py-2 pr-4">Visiteur</th>
                <th className="py-2 pr-4">Engagement</th>
                <th className="py-2 pr-4">Visites</th>
                <th className="py-2 pr-4">Temps</th>
                <th className="py-2 pr-4">Actions</th>
                <th className="py-2 pr-4">Dernière visite</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.visitor_id} data-testid={`activity-row-${r.visitor_id}`} className="border-b border-[#012141]/5 hover:bg-[#012141]/[0.02]">
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-2">
                      {r.identified ? <UserCheck size={15} className="text-[#1F7A5C]" /> : <UserX size={15} className="text-slate-300" />}
                      <span className="font-semibold text-[#012141]">{r.prenom || "Visiteur anonyme"}</span>
                    </div>
                    {r.source && <div className="text-[11px] text-slate-400 mt-0.5">{r.source}</div>}
                  </td>
                  <td className="py-2.5 pr-4"><ScoreBadge score={r.score} temperature={r.temperature} /></td>
                  <td className="py-2.5 pr-4 text-slate-600">{r.visits}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{fmtTime(r.total_time_ms)}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{r.events_count}</td>
                  <td className="py-2.5 pr-4 text-slate-400">{fmtDate(r.last_seen)}</td>
                  <td className="py-2.5 pr-4">
                    <button data-testid={`activity-view-${r.visitor_id}`} onClick={() => openDetail(r.visitor_id)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#012141] border border-[#012141]/15 rounded-full px-3 py-1.5 hover:bg-[#012141]/5 transition-colors">
                      <Eye size={13} /> Détail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-slate-400 text-sm py-6 text-center">Aucun visiteur dans cette catégorie.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="activity-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#012141] flex items-center gap-2">
              <Activity size={18} className="text-[#C89A3E]" /> {detail?.prenom || "Visiteur anonyme"}
            </DialogTitle>
          </DialogHeader>

          {!detail ? (
            <p className="text-slate-400 text-sm py-8 text-center">Chargement…</p>
          ) : (
            <div className="space-y-5">
              {/* Résumé engagement */}
              <div className="rounded-2xl bg-[#012141] text-white p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/60">Score d'engagement</div>
                    <div className="text-4xl font-bold font-heading text-[#F5C64E]">{detail.engagement.score}<span className="text-lg text-white/50"> / 100</span></div>
                  </div>
                  <ScoreBadge score={detail.engagement.score} temperature={detail.engagement.temperature} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded-xl bg-white/10 py-2"><div className="text-white/60">Visites</div><div className="font-bold text-base">{detail.visits}</div></div>
                  <div className="rounded-xl bg-white/10 py-2"><div className="text-white/60">Temps total</div><div className="font-bold text-base">{fmtTime(detail.total_time_ms)}</div></div>
                  <div className="rounded-xl bg-white/10 py-2"><div className="text-white/60">Actions</div><div className="font-bold text-base">{detail.events_count}</div></div>
                </div>
                {Object.keys(detail.engagement.breakdown || {}).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(detail.engagement.breakdown).map(([k, v]) => (
                      <span key={k} className="text-[11px] bg-white/10 rounded-full px-2 py-0.5">{k} <span className="text-[#F5C64E] font-bold">+{v}</span></span>
                    ))}
                  </div>
                )}
                <div className="mt-2 text-[11px] text-white/50">Facteur récence appliqué : ×{detail.engagement.recency_factor}</div>
              </div>

              {/* Contact */}
              {detail.lead && (
                <div className="rounded-2xl border border-[#012141]/10 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-[#C89A3E] mb-2">Coordonnées</div>
                  <div className="text-sm text-[#012141] space-y-0.5">
                    <div><span className="text-slate-400">Prénom :</span> <b>{detail.lead.prenom}</b></div>
                    <div><span className="text-slate-400">Courriel :</span> {detail.lead.courriel || "—"}</div>
                    <div><span className="text-slate-400">Téléphone :</span> {detail.lead.telephone || "—"}</div>
                    <div><span className="text-slate-400">Provenance :</span> {detail.lead.source || "—"}</div>
                  </div>
                </div>
              )}

              {/* Carte de protection */}
              {detail.diagnostic && (
                <div className="rounded-2xl border border-[#012141]/10 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={16} className="text-[#C89A3E]" />
                    <div className="text-xs font-bold uppercase tracking-wide text-[#C89A3E]">Carte de protection</div>
                    <div className="ml-auto flex items-center gap-2">
                      {detail.diagnostic.score != null && <span className="text-sm font-bold text-[#012141]">{detail.diagnostic.score}% couvert</span>}
                      <button data-testid="download-card-btn" onClick={() => downloadCard(detail.visitor_id, detail.prenom)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#012141] bg-[#C89A3E]/20 hover:bg-[#C89A3E]/35 rounded-full px-3 py-1.5 transition-colors">
                        <Download size={13} /> Télécharger
                      </button>
                      {detail.lead?.courriel && (
                        <button data-testid="email-card-btn" disabled={sendingCard} onClick={() => emailCard(detail.visitor_id)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#012141] hover:bg-[#00172E] disabled:opacity-60 rounded-full px-3 py-1.5 transition-colors">
                          {sendingCard ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Envoyer au visiteur
                        </button>
                      )}
                    </div>
                  </div>
                  {detail.diagnostic.family && <div className="text-sm text-slate-600 mb-3">Profil : <b className="text-[#012141] capitalize">{detail.diagnostic.family}</b></div>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] font-bold uppercase text-[#1F7A5C] mb-1.5 flex items-center gap-1"><Check size={12} /> Forces</div>
                      {(detail.diagnostic.strengths || []).length === 0 ? <p className="text-xs text-slate-400">—</p> :
                        (detail.diagnostic.strengths || []).map((s, i) => <div key={i} className="text-xs text-slate-600 mb-1">• {s.code || s.message}</div>)}
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase text-[#C89A3E] mb-1.5 flex items-center gap-1"><Triangle size={11} /> À renforcer</div>
                      {(detail.diagnostic.gaps || []).length === 0 ? <p className="text-xs text-slate-400">—</p> :
                        (detail.diagnostic.gaps || []).map((g, i) => <div key={i} className="text-xs text-slate-600 mb-1">• {g.code || g.message}</div>)}
                    </div>
                  </div>
                </div>
              )}
              {!detail.diagnostic && (
                <div className="rounded-2xl border border-dashed border-[#012141]/15 p-4 text-sm text-slate-400" data-testid="no-diagnostic-note">
                  Ce visiteur n'a pas encore complété de diagnostic — aucune carte de protection à générer pour l'instant.
                </div>
              )}

              {/* Chronologie */}
              <div className="rounded-2xl border border-[#012141]/10 p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-[#C89A3E] mb-3 flex items-center gap-1"><MousePointerClick size={13} /> Chronologie d'activité</div>
                {detail.events.length === 0 ? <p className="text-xs text-slate-400">Aucune action détaillée.</p> : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {detail.events.map((e, i) => (
                      <div key={i} className="flex items-start gap-2.5 text-sm">
                        <span className="mt-1 w-2 h-2 rounded-full bg-[#C89A3E] flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-semibold text-[#012141]">{e.label_fr}{e.label ? ` · ${e.label}` : ""}</div>
                          <div className="text-[11px] text-slate-400 flex items-center gap-1"><Clock size={10} /> {fmtDate(e.created_at)}{e.path ? ` · ${e.path}` : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
